import React from 'react';
import { ATSReport, SectionRecommendation } from '@/api/types';
import Markdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';

interface OverviewTabProps {
  report: ATSReport;
  recommendations: SectionRecommendation[];
}

export function AnalysisOverviewTab({ report, recommendations }: OverviewTabProps) {
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="p-4 bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-800 rounded-lg text-center flex flex-col items-center justify-center">
          <div className="text-3xl font-mono text-slate-800 dark:text-slate-300 flex items-baseline justify-center gap-1">
            {Math.round(report.estimatedScore)} <span className="text-sm text-slate-400 dark:text-slate-500">/100</span>
          </div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mt-2">ATS Score</div>
        </div>
        <div className="p-4 bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-800 rounded-lg text-center flex flex-col items-center justify-center">
          <div className="text-3xl font-mono text-blue-600 dark:text-blue-400 flex items-baseline justify-center gap-1">
            {Math.round(report.matchingScore || report.estimatedScore)} <span className="text-sm text-blue-400/60 dark:text-blue-500/60">/100</span>
          </div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mt-2">Match Score</div>
        </div>
        <div className="p-4 bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-800 rounded-lg text-center flex flex-col items-center justify-center">
          <div className="text-3xl font-mono text-emerald-600 dark:text-emerald-400">{report.matchedKeywords.length}</div>
          <div className="text-[10px] font-semibold uppercase tracking-wider mt-2 text-slate-500 dark:text-slate-400">Matched</div>
        </div>
        <div className="p-4 bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-800 rounded-lg text-center flex flex-col items-center justify-center">
          <div className="text-3xl font-mono text-rose-600 dark:text-rose-400">{report.missingKeywords.length}</div>
          <div className="text-[10px] font-semibold uppercase tracking-wider mt-2 text-slate-500 dark:text-slate-400">Missing</div>
        </div>
        <div className="p-4 bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-800 rounded-lg text-center flex flex-col items-center justify-center">
          <div className="text-3xl font-mono text-slate-800 dark:text-slate-300">{recommendations.length}</div>
          <div className="text-[10px] font-semibold uppercase tracking-wider mt-2 text-slate-500 dark:text-slate-400">Updates</div>
        </div>
      </div>

      {report.industryFormatMatch && (
        <div className="mt-6 p-5 bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-800 rounded-lg">
          <h3 className="text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-2 text-slate-600 dark:text-slate-400">
            <span className="w-1.5 h-1.5 rounded-full bg-white"></span>
            Industry Standard Check
          </h3>
          <p className="text-sm font-sans text-slate-700 dark:text-slate-300 leading-relaxed">
            {report.industryFormatMatch}
          </p>
        </div>
      )}

      {recommendations.length > 0 && (
        <div className="mt-8">
          <h3 className="text-xs font-semibold uppercase tracking-wider mb-4 text-slate-600 dark:text-slate-400">Top Priorities</h3>
          <div className="space-y-4">
            {recommendations.filter(r => r.priority === 'high').map((rec, i) => (
              <div key={i} className="flex gap-4 items-start p-4 bg-white dark:bg-slate-950 border border-rose-500/20 rounded-lg">
                <div className="w-full">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="px-2 py-0.5 bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/20 text-rose-700 dark:text-rose-400 text-[10px] font-bold uppercase tracking-wider rounded">HIGH</span>
                    <div className="text-sm font-medium text-slate-700 dark:text-slate-300">{(rec as any).title || rec.section}</div>
                  </div>
                  <div className="text-sm font-mono text-slate-600 dark:text-slate-400 mt-2 leading-relaxed max-w-none prose prose-sm dark:prose-invert">
                    <Markdown rehypePlugins={[rehypeSanitize]}>{rec.issue}</Markdown>
                  </div>
                </div>
              </div>
            ))}
            {recommendations.filter(r => r.priority === 'high').length === 0 && (
              <div className="text-sm text-slate-600 dark:text-slate-400">No high priority recommended changes.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
